import { useNavigate } from "@tanstack/react-router";
import { useQueries } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Copy,
  Package,
  Plus,
  TriangleAlertIcon,
} from "@/components/icons";
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from "@/components/kit";
import {
  FieldContent,
  FieldDescription,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useVariantIncomingStock } from "@/modules/inventory/presentation/api/import-orders-queries";
import { inventoryApi } from "@/modules/inventory/presentation/api/client";
import { useVariantStockLotsQuery } from "@/modules/inventory/presentation/api/queries";
import { formatLakAmount } from "@/modules/inventory/presentation/lib/import-order-labels";
import { VariantImportPriceHistoryPanel } from "@/modules/inventory/presentation/ui/VariantImportPriceHistoryPanel";
import { VariantStockLotsPanel } from "@/modules/inventory/presentation/ui/VariantStockLotsPanel";
import {
  type OrderLineLotPick,
  sumLotPickQuantity,
  toApiLotPicks,
} from "@/modules/orders/presentation/lib/order-lot-allocation";
import { variantStockAlertStatus } from "@/modules/products/domain/low-stock";
import type { ProductDetailDTO } from "@/modules/products/domain/types";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatLak } from "@/shared/lib/currency";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useAdjustStock } from "../api/queries";
import {
  isStockAdjustLotSelectionValid,
  StockAdjustLotPicker,
} from "./StockAdjustLotPicker";
import { ProductStockWithoutLotAlert } from "./ProductStockWithoutLotAlert";

type ProductVariant = ProductDetailDTO["variants"][number];

type VariantSortKey = "name" | "remaining";

const VARIANT_SORT_OPTIONS: { value: VariantSortKey; label: string }[] = [
  { value: "name", label: "ຊື່" },
  { value: "remaining", label: "ຈຳນວນຄົງເຫຼືອ" },
];

function variantRemainingQty(variant: ProductVariant): number {
  return variant.saleableStockQuantity ?? variant.stockQuantity;
}

function sortVariants(
  variants: ProductVariant[],
  sortKey: VariantSortKey,
): ProductVariant[] {
  const sorted = [...variants];
  if (sortKey === "name") {
    sorted.sort((a, b) => a.name.localeCompare(b.name, "lo"));
    return sorted;
  }
  sorted.sort(
    (a, b) => variantRemainingQty(b) - variantRemainingQty(a),
  );
  return sorted;
}

type ProductStockPanelProps = {
  product: ProductDetailDTO;
  initialAssignVariantId?: string;
  onAssignDialogOpened?: () => void;
  onStockAdjusted?: () => void;
};

type StockDialogState = {
  variantId: string;
  name: string;
  stockUnitLabel: string;
  stockQuantity: number;
  delta: string;
};

type IncomingRow = {
  variantId: string;
  incomingQuantity: number;
  inTransitValueLak: string;
};

function SummaryStat({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "warn" | "danger" | "ok";
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-xl border px-4 py-3",
        tone === "danger" && "border-destructive/25 bg-destructive/5",
        tone === "warn" && "border-secondary/30 bg-secondary/5",
        tone === "ok" && "border-leaf/30 bg-leaf/5",
        tone === "default" && "border-secondary/15 bg-background/60",
      )}
    >
      <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className="font-semibold text-foreground text-lg tabular-nums leading-none">
        {value}
      </span>
      {hint ? (
        <span className="text-muted-foreground text-xs">{hint}</span>
      ) : null}
    </div>
  );
}

function StockLevelBar({
  qty,
  incomingQty = 0,
  threshold,
  unitLabel,
}: {
  qty: number;
  incomingQty?: number;
  threshold: number;
  unitLabel: string;
}) {
  const status = variantStockAlertStatus(qty, threshold);
  const total = qty + Math.max(0, incomingQty);
  const scale = Math.max(threshold * 3, total, 1);
  const stockPct = Math.min(100, Math.round((qty / scale) * 100));
  const incomingPct =
    incomingQty > 0
      ? Math.min(100 - stockPct, Math.round((incomingQty / scale) * 100))
      : 0;
  const thresholdPct =
    threshold > 0 ? Math.min(100, Math.round((threshold / scale) * 100)) : null;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 text-xs">
        <span className="text-muted-foreground">ລະດັບສະຕັອກ</span>
        <span className="tabular-nums">
          <span className="font-medium text-foreground">{qty}</span>
          <span className="text-muted-foreground"> {unitLabel}</span>
          {incomingQty > 0 ? (
            <span className="text-secondary">
              {" "}
              + {incomingQty} ລໍຖ້າຮັບ
            </span>
          ) : null}
          {threshold > 0 ? (
            <span className="text-muted-foreground"> · ເຕືອນ ≤ {threshold}</span>
          ) : null}
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-muted/60">
        <div className="flex h-full w-full">
          <div
            className={cn(
              "h-full shrink-0 transition-[width]",
              status === "out" && "bg-destructive/70",
              status === "low" && "bg-secondary",
              status === "ok" && "bg-leaf",
            )}
            style={{ width: `${stockPct}%` }}
          />
          {incomingPct > 0 ? (
            <div
              className="h-full shrink-0 bg-primary/55"
              style={{ width: `${incomingPct}%` }}
              title={`ລໍຖ້າຮັບ ${incomingQty} ${unitLabel}`}
            />
          ) : null}
        </div>
        {thresholdPct != null ? (
          <div
            className="absolute top-0 bottom-0 z-10 w-px bg-foreground/35"
            style={{ left: `${thresholdPct}%` }}
            title={`ເຕືອນ ${threshold}`}
          />
        ) : null}
      </div>
      {incomingQty > 0 ? (
        <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                "size-2 rounded-full",
                status === "out" && "bg-destructive/70",
                status === "low" && "bg-secondary",
                status === "ok" && "bg-leaf",
              )}
            />
            ຄົງມື
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-primary/55 ring-2 ring-primary/20" />
            ສັ່ງແລ້ວຍັງບໍ່ຮັບ
          </span>
        </div>
      ) : null}
    </div>
  );
}

function resolveIncomingDisplayQty(
  variant: ProductVariant,
  pool: ProductVariant | undefined,
  incoming?: IncomingRow,
): number {
  if (!incoming || incoming.incomingQuantity <= 0) return 0;
  if (variant.inventoryVariantId && pool) {
    const unitsPerSale = Math.max(Number(variant.unitsPerSale) || 1, 1);
    return Math.floor(incoming.incomingQuantity / unitsPerSale);
  }
  return incoming.incomingQuantity;
}

function VariantGroup({
  title,
  description,
  children,
  count,
}: {
  title: string;
  description: string;
  children: ReactNode;
  count: number;
}) {
  if (count === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <div className="px-0.5">
        <div className="flex items-baseline gap-2">
          <h3 className="font-medium text-sm">{title}</h3>
          <span className="text-muted-foreground text-xs tabular-nums">
            {count}
          </span>
        </div>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function VariantStockCard({
  product,
  variant: v,
  incoming,
  canAdjust,
  canViewIncoming,
  expanded,
  onExpandedChange,
  onOpenAdjust,
  onConvertUnit,
}: {
  product: ProductDetailDTO;
  variant: ProductVariant;
  incoming?: IncomingRow;
  canAdjust: boolean;
  canViewIncoming: boolean;
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
  onOpenAdjust: () => void;
  onConvertUnit: () => void;
}) {
  const displayQty = v.saleableStockQuantity ?? v.stockQuantity;
  const isPool = v.isInventoryOnly && !v.inventoryVariantId;
  const isSaleLinked = Boolean(v.inventoryVariantId);
  const isBundle = v.variantType === "bundle";
  const lotVariantId = v.inventoryVariantId ?? v.id;
  const pool = product.variants.find((p) => p.id === v.inventoryVariantId);
  const lotStockQty = isSaleLinked
    ? (v.poolStockQuantity ?? 0)
    : v.stockQuantity;
  const lotUnitLabel = isSaleLinked
    ? (pool?.stockUnitLabel ?? v.stockUnitLabel)
    : v.stockUnitLabel;
  const incomingDisplayQty = resolveIncomingDisplayQty(v, pool, incoming);
  const alertIncomingQty = canViewIncoming ? incomingDisplayQty : 0;
  const barQty = isSaleLinked ? displayQty : lotStockQty;
  const status = variantStockAlertStatus(
    displayQty,
    v.lowStockThreshold,
    alertIncomingQty,
  );
  const isOut = status === "out";
  const isLow = status === "low";

  return (
    <article
      className={cn(
        "overflow-hidden rounded-2xl border bg-card/50 shadow-sm",
        isOut
          ? "border-destructive/25"
          : isLow
            ? "border-secondary/35"
            : "border-secondary/20",
      )}
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-secondary">
              <Package className="size-4" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-medium text-sm sm:text-base">{v.name}</h4>
                {isPool ? (
                  <Badge variant="secondary">ຄລັງຮ່ວມ</Badge>
                ) : isSaleLinked ? (
                  <Badge className="border-leaf/30 bg-leaf/10 text-leaf">
                    ໃຊ້ຄລັງຮ່ວມ
                  </Badge>
                ) : isBundle ? (
                  <Badge className="border-amber-600/30 bg-amber-500/10 text-amber-800">
                    ເຊັດ
                  </Badge>
                ) : null}
                {isOut ? (
                  <Badge variant="outline">ຫມົດ</Badge>
                ) : isLow ? (
                  <Badge variant="destructive">ໃກ້ໝົດ</Badge>
                ) : (
                  <Badge className="border-leaf/30 bg-leaf/10 text-leaf">
                    ພ້ອມຂາຍ
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground text-xs sm:text-sm">
                {isPool
                  ? `ຮັບເຂົ້າ · ${formatQtyWithUnit(v.stockQuantity, v.stockUnitLabel)}`
                  : `ລາຄາຂາຍ ${formatLak(v.price)}/${v.stockUnitLabel}`}
              </p>
              {isSaleLinked && pool ? (
                <p className="text-muted-foreground text-xs">
                  ດຶງຈາກ «{pool.name}» · {Number(v.unitsPerSale)}{" "}
                  {pool.stockUnitLabel}/ຫນ່ວຍຂາຍ
                  {v.poolStockQuantity != null
                    ? ` · ຄລັງ ${formatQtyWithUnit(v.poolStockQuantity, pool.stockUnitLabel)}`
                    : ""}
                </p>
              ) : null}
              {incoming && incoming.incomingQuantity > 0 && canViewIncoming ? (
                <p className="text-muted-foreground text-xs">
                  ມູນຄ່າລໍຖ້າຮັບ ~{formatLakAmount(incoming.inTransitValueLak)}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            <div className="rounded-xl border border-secondary/20 bg-background/80 px-3 py-1.5 text-right">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                {isPool ? "ຄລັງ" : "ຂາຍໄດ້"}
              </p>
              <p className="font-display text-foreground text-xl tabular-nums leading-tight sm:text-2xl">
                {displayQty}
                <span className="ml-1 font-sans text-muted-foreground text-xs">
                  {v.stockUnitLabel}
                </span>
              </p>
            </div>
            {canAdjust && !isSaleLinked ? (
              <>
                {!isPool && !isBundle ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-secondary/40"
                    onClick={onConvertUnit}
                  >
                    <Copy data-icon="inline-start" />
                    ແປງໜ່ວຍ
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-secondary/40"
                  onClick={onOpenAdjust}
                >
                  <Package data-icon="inline-start" />
                  ປັບສະຕັອກ
                </Button>
              </>
            ) : null}
          </div>
        </div>

        <StockLevelBar
          qty={barQty}
          incomingQty={canViewIncoming ? incomingDisplayQty : 0}
          threshold={v.lowStockThreshold}
          unitLabel={v.stockUnitLabel}
        />

        <Collapsible open={expanded} onOpenChange={onExpandedChange}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-between rounded-lg px-2 text-muted-foreground hover:text-foreground"
            >
              <span className="text-xs">
                {expanded ? "ຊ່ອນ lot & ລາຍລະອຽດ" : "ເບິ່ງ lot & ລາຍລະອຽດ"}
              </span>
              <ChevronDown
                className={cn(
                  "size-4 transition-transform",
                  expanded && "rotate-180",
                )}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-4 rounded-xl border border-secondary/15 bg-background/50 px-3 py-3">
              <VariantStockLotsPanel
                embedded
                variantId={lotVariantId}
                stockQuantity={lotStockQty}
                stockUnitLabel={lotUnitLabel}
              />
              {canViewIncoming && !isSaleLinked ? (
                <div className="border-secondary/15 border-t pt-3">
                  <VariantImportPriceHistoryPanel
                    embedded
                    variantId={lotVariantId}
                  />
                </div>
              ) : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </article>
  );
}

export function ProductStockPanel({
  product,
  initialAssignVariantId,
  onAssignDialogOpened,
  onStockAdjusted,
}: ProductStockPanelProps) {
  const navReceive = useNavigate();
  const adjustStock = useAdjustStock();
  const canReceive = useActionPermission("inventory:write");
  const canAdjust = useActionPermission("products:write");
  const canViewIncoming = useActionPermission("import_orders:read");
  const openedAssignRef = useRef<string | null>(null);

  const poolVariantsForLots = useMemo(
    () =>
      product.variants.filter(
        (v) => !v.inventoryVariantId && v.stockQuantity > 0,
      ),
    [product.variants],
  );

  const poolLotQueries = useQueries({
    queries: poolVariantsForLots.map((variant) => ({
      queryKey: ["inventory", "variant-lots", variant.id, false] as const,
      queryFn: () => inventoryApi.getVariantStockLots(variant.id),
    })),
  });

  const withoutLotRows = useMemo(() => {
    return poolVariantsForLots.flatMap((variant, index) => {
      const query = poolLotQueries[index];
      if (!query || query.isLoading) return [];
      const lots = query.data?.data ?? [];
      const lotTotal = lots.reduce(
        (sum, lot) => sum + lot.quantityRemaining,
        0,
      );
      const withoutLotQuantity = variant.stockQuantity - lotTotal;
      if (withoutLotQuantity <= 0) return [];
      return [{ variant, withoutLotQuantity }];
    });
  }, [poolVariantsForLots, poolLotQueries]);

  const withoutLotLoading =
    poolVariantsForLots.length > 0 &&
    poolLotQueries.some((query) => query.isLoading);

  const poolVariantIds = useMemo(
    () =>
      product.variants.filter((v) => !v.inventoryVariantId).map((v) => v.id),
    [product.variants],
  );
  const incomingQuery = useVariantIncomingStock(
    canViewIncoming ? poolVariantIds : [],
  );
  const incomingByVariant = useMemo(
    () =>
      new Map<string, IncomingRow>(
        (incomingQuery.data?.data ?? []).map((row) => [row.variantId, row]),
      ),
    [incomingQuery.data],
  );
  const totalIncomingQty = useMemo(
    () =>
      (incomingQuery.data?.data ?? []).reduce(
        (sum, row) => sum + row.incomingQuantity,
        0,
      ),
    [incomingQuery.data],
  );

  const [stockDialog, setStockDialog] = useState<StockDialogState | null>(null);
  const [reduceLotPicks, setReduceLotPicks] = useState<OrderLineLotPick[]>([]);
  const [addLotPicks, setAddLotPicks] = useState<OrderLineLotPick[]>([]);
  const [assignLotPicks, setAssignLotPicks] = useState<OrderLineLotPick[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [sortKey, setSortKey] = useState<VariantSortKey>("name");

  const activeLots = useVariantStockLotsQuery(stockDialog?.variantId ?? "");
  const lotRows = activeLots.data?.data ?? [];
  const lotTotal = useMemo(
    () => lotRows.reduce((sum, lot) => sum + lot.quantityRemaining, 0),
    [lotRows],
  );
  const withoutLot = stockDialog
    ? Math.max(0, stockDialog.stockQuantity - lotTotal)
    : 0;

  const adjustQty =
    stockDialog && Number.isFinite(Number(stockDialog.delta))
      ? Number(stockDialog.delta)
      : 0;
  const reduceQty = adjustQty < 0 ? Math.abs(adjustQty) : 0;
  const addQty = adjustQty > 0 ? adjustQty : 0;
  const addPickSum = sumLotPickQuantity(addLotPicks);
  const assignPickSum = sumLotPickQuantity(assignLotPicks);

  const reduceLotSelectionValid = stockDialog
    ? isStockAdjustLotSelectionValid(
        "reduce",
        stockDialog.stockQuantity,
        lotTotal,
        reduceQty,
        reduceLotPicks,
      )
    : false;

  const addLotSelectionValid = stockDialog
    ? isStockAdjustLotSelectionValid(
        "add",
        stockDialog.stockQuantity,
        lotTotal,
        addQty,
        addLotPicks,
      )
    : false;

  const assignLotSelectionValid = stockDialog
    ? isStockAdjustLotSelectionValid(
        "assign",
        stockDialog.stockQuantity,
        lotTotal,
        withoutLot,
        assignLotPicks,
      )
    : false;

  const canSubmitAdjust =
    stockDialog &&
    Number.isFinite(adjustQty) &&
    adjustQty !== 0 &&
    (adjustQty > 0
      ? addLotSelectionValid
      : reduceLotSelectionValid && reduceQty <= stockDialog.stockQuantity);

  const canSubmitAssign =
    stockDialog &&
    withoutLot > 0 &&
    lotRows.length > 0 &&
    assignLotSelectionValid &&
    assignPickSum > 0;

  const summary = useMemo(() => {
    const inventoryVariants = product.variants.filter(
      (v) => !v.inventoryVariantId,
    );
    const totalStock = inventoryVariants.reduce(
      (sum, v) => sum + v.stockQuantity,
      0,
    );
    const countAlertStatus = (target: "out" | "low") =>
      product.variants.filter((v) => {
        if (!v.isActive) return false;
        const qty = v.saleableStockQuantity ?? v.stockQuantity;
        const pool = v.inventoryVariantId
          ? product.variants.find((p) => p.id === v.inventoryVariantId)
          : undefined;
        const incomingRow = incomingByVariant.get(v.inventoryVariantId ?? v.id);
        const incoming = canViewIncoming
          ? resolveIncomingDisplayQty(v, pool, incomingRow)
          : 0;
        return (
          variantStockAlertStatus(qty, v.lowStockThreshold, incoming) === target
        );
      }).length;
    const outCount = countAlertStatus("out");
    const lowCount = countAlertStatus("low");
    const unitLabel =
      inventoryVariants.length === 1
        ? inventoryVariants[0]?.stockUnitLabel
        : "ຊິ້ນ";
    return { totalStock, outCount, lowCount, unitLabel };
  }, [product, incomingByVariant, canViewIncoming]);

  const grouped = useMemo(() => {
    const pools: ProductVariant[] = [];
    const linked: ProductVariant[] = [];
    const standalone: ProductVariant[] = [];
    for (const v of product.variants) {
      if (v.isInventoryOnly && !v.inventoryVariantId) pools.push(v);
      else if (v.inventoryVariantId) linked.push(v);
      else standalone.push(v);
    }
    return {
      pools: sortVariants(pools, sortKey),
      linked: sortVariants(linked, sortKey),
      standalone: sortVariants(standalone, sortKey),
    };
  }, [product.variants, sortKey]);

  const sortedVariants = useMemo(
    () => sortVariants(product.variants, sortKey),
    [product.variants, sortKey],
  );

  function setExpanded(id: string, open: boolean) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function openStockDialog(
    variant: ProductVariant,
    options?: { assignOnly?: boolean },
  ) {
    if (variant.inventoryVariantId) return;
    setReduceLotPicks([]);
    setAddLotPicks([]);
    setAssignLotPicks([]);
    setStockDialog({
      variantId: variant.id,
      name: variant.name,
      stockUnitLabel: variant.stockUnitLabel,
      stockQuantity: variant.stockQuantity,
      delta: options?.assignOnly ? "0" : "-1",
    });
  }

  function openAssignDialog(variantId: string) {
    const variant = product.variants.find((v) => v.id === variantId);
    if (!variant) return;
    openStockDialog(variant, { assignOnly: true });
  }

  useEffect(() => {
    if (!initialAssignVariantId || !canAdjust) return;
    if (openedAssignRef.current === initialAssignVariantId) return;
    if (withoutLotLoading) return;

    const variant = product.variants.find((v) => v.id === initialAssignVariantId);
    if (!variant || variant.inventoryVariantId) return;

    openedAssignRef.current = initialAssignVariantId;
    openStockDialog(variant, { assignOnly: true });
    onAssignDialogOpened?.();
  }, [
    initialAssignVariantId,
    canAdjust,
    product.variants,
    withoutLotLoading,
    onAssignDialogOpened,
  ]);

  function closeStockDialog() {
    setStockDialog(null);
    setReduceLotPicks([]);
    setAddLotPicks([]);
    setAssignLotPicks([]);
  }

  function renderCard(v: ProductVariant) {
    const lotVariantId = v.inventoryVariantId ?? v.id;
    return (
      <VariantStockCard
        key={v.id}
        product={product}
        variant={v}
        incoming={incomingByVariant.get(lotVariantId)}
        canAdjust={!!canAdjust}
        canViewIncoming={!!canViewIncoming}
        expanded={expandedIds.has(v.id)}
        onExpandedChange={(open) => setExpanded(v.id, open)}
        onOpenAdjust={() => openStockDialog(v)}
        onConvertUnit={() =>
          navReceive({
            to: "/app/products/$id/duplicate-convert",
            params: { id: product.id },
            search: { variantId: v.id },
          })
        }
      />
    );
  }

  const multiGroup =
    [grouped.pools, grouped.linked, grouped.standalone].filter(
      (g) => g.length > 0,
    ).length > 1;

  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            <SummaryStat
              label="ສະຕັອກລວມ"
              value={formatQtyWithUnit(summary.totalStock, summary.unitLabel)}
              hint={`${product.variants.length} ຕົວເລືອກ`}
            />
            {canViewIncoming && totalIncomingQty > 0 ? (
              <SummaryStat
                label="ລໍຖ້າຮັບເຂົ້າ"
                value={formatQtyWithUnit(totalIncomingQty, summary.unitLabel)}
                hint="ຈາກໃບສັ່ງນຳເຂົ້າ"
                tone="warn"
              />
            ) : null}
            <SummaryStat
              label="ຕົວເລືອກ"
              value={String(product.variants.length)}
            />
            <SummaryStat
              label="ໃກ້ໝົດ"
              value={String(summary.lowCount)}
              tone={summary.lowCount > 0 ? "warn" : "default"}
            />
            <SummaryStat
              label="ຫມົດ"
              value={String(summary.outCount)}
              tone={summary.outCount > 0 ? "danger" : "default"}
            />
          </div>
          {canReceive ? (
            <Button
              className="shrink-0 rounded-full"
              onClick={() => navReceive({ to: "/app/inventory/receive" })}
            >
              <Plus data-icon="inline-start" />
              ຮັບເຂົ້າ lot
            </Button>
          ) : null}
        </div>

        <ProductStockWithoutLotAlert
          rows={withoutLotRows}
          isLoading={withoutLotLoading}
          canManage={!!canAdjust}
          onManage={openAssignDialog}
        />

        <div className="rounded-xl border border-secondary/20 bg-secondary/5 px-4 py-3 text-muted-foreground text-xs leading-relaxed">
          <strong className="text-foreground">ພາບລວມ</strong> —
          ເບິ່ງຄົງເຫຼືອແຕ່ລະຕົວເລືອກກ່ອນ ແລ້ວກົດ «ເບິ່ງ lot» ເມື່ອຕ້ອງການລາຍລະອຽດ ·{" "}
          <strong className="text-foreground">ເພີ່ມ (+)</strong> ປັບມື /{" "}
          <strong className="text-foreground">ຫຼຸດ (−)</strong> ເລືອກ lot · lot +
          ຕົ້ນທຸນ ໃຊ້{" "}
          <button
            type="button"
            className="font-medium text-secondary underline-offset-2 hover:underline"
            onClick={() => navReceive({ to: "/app/inventory/receive" })}
          >
            ຮັບເຂົ້າ lot
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-muted-foreground text-xs">ຈັດລຽງ</span>
          <SimpleSelect
            value={sortKey}
            onValueChange={(value) => setSortKey(value as VariantSortKey)}
            options={VARIANT_SORT_OPTIONS}
            className="h-9 w-[min(100%,12rem)] rounded-full"
          />
        </div>

        <div className="flex flex-col gap-6">
          {multiGroup ? (
            <>
              <VariantGroup
                title="ຄລັງຮ່ວມ"
                description="ສະຕັອກຈິງທີ່ຮັບເຂົ້າ — ແບ່ງໃຫ້ຫຼາຍຂະໜາດຂາຍ"
                count={grouped.pools.length}
              >
                {grouped.pools.map(renderCard)}
              </VariantGroup>
              <VariantGroup
                title="ຂາຍ (ດຶງຄລັງຮ່ວມ)"
                description="ຈຳນວນຂາຍໄດ້ຄິດໄລ່ຈາກຄລັງ — ບໍ່ມີ lot ແຍກ"
                count={grouped.linked.length}
              >
                {grouped.linked.map(renderCard)}
              </VariantGroup>
              <VariantGroup
                title="ຊິ້ນເດີຍວ / ເຊັດ"
                description="ສະຕັອກ ແລະ lot ຂອງຕົນເອງ"
                count={grouped.standalone.length}
              >
                {grouped.standalone.map(renderCard)}
              </VariantGroup>
            </>
          ) : (
            <div className="flex flex-col gap-3">
              {sortedVariants.map(renderCard)}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={stockDialog !== null}
        onOpenChange={(open) => {
          if (!open) closeStockDialog();
        }}
      >
        <DialogContent className="flex max-h-[min(90dvh,720px)] flex-col gap-4 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">ປັບສະຕັອກ</DialogTitle>
          </DialogHeader>
          {stockDialog ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              <div className="rounded-xl border border-secondary/20 bg-background/60 p-4">
                <p className="font-medium">{stockDialog.name}</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  ຄົງເຫຼືອ{" "}
                  <span className="font-semibold text-foreground tabular-nums">
                    {formatQtyWithUnit(
                      stockDialog.stockQuantity,
                      stockDialog.stockUnitLabel,
                    )}
                  </span>
                </p>
                {withoutLot > 0 ? (
                  <p className="mt-1 text-muted-foreground text-xs">
                    ບໍ່ມີ lot:{" "}
                    {formatQtyWithUnit(withoutLot, stockDialog.stockUnitLabel)}
                  </p>
                ) : null}
              </div>

              <FieldLayout>
                <FieldLabel htmlFor="stock-delta">ຈຳນວນທີ່ປັບ (+ / −)</FieldLabel>
                <FieldContent>
                  <Input
                    id="stock-delta"
                    type="number"
                    min={-stockDialog.stockQuantity}
                    value={stockDialog.delta}
                    onChange={(e) => {
                      setStockDialog({
                        ...stockDialog,
                        delta: e.target.value,
                      });
                      setAddLotPicks([]);
                    }}
                    className="text-lg tabular-nums"
                  />
                  <FieldDescription>
                    ໃສ່ + ເພື່ອເພີ່ມ (ເຊັ່ນ 5) ຫຼື − ເພື່ອຫຼຸດ (ເຊັ່ນ -2)
                  </FieldDescription>
                </FieldContent>
              </FieldLayout>

              {addQty > 0 && lotRows.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="font-medium text-sm">ເລືອກ lot ທີ່ຈະເພີ່ມ</p>
                  <StockAdjustLotPicker
                    mode="add"
                    variantId={stockDialog.variantId}
                    stockUnitLabel={stockDialog.stockUnitLabel}
                    stockQuantity={stockDialog.stockQuantity}
                    targetQty={addQty}
                    picks={addLotPicks}
                    onPicksChange={setAddLotPicks}
                  />
                </div>
              ) : addQty > 0 ? (
                <p className="flex items-start gap-2 rounded-xl border border-leaf/30 bg-leaf/5 p-3 text-muted-foreground text-xs">
                  <Plus className="mt-0.5 size-4 shrink-0 text-leaf" />
                  ຈະເພີ່ມ {formatQtyWithUnit(addQty, stockDialog.stockUnitLabel)}{" "}
                  ເຂົ້າສະຕັອກທີ່ບໍ່ມີ lot — ຖ້າຕ້ອງການ lot + ຕົ້ນທຸນ ໃຊ້{" "}
                  <button
                    type="button"
                    className="font-medium text-secondary underline-offset-2 hover:underline"
                    onClick={() => {
                      closeStockDialog();
                      navReceive({ to: "/app/inventory/receive" });
                    }}
                  >
                    ຮັບເຂົ້າ lot
                  </button>
                </p>
              ) : null}

              {reduceQty > 0 && lotRows.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <p className="font-medium text-sm">ເລືອກ lot ທີ່ຈະຫຼຸດ</p>
                  <StockAdjustLotPicker
                    mode="reduce"
                    variantId={stockDialog.variantId}
                    stockUnitLabel={stockDialog.stockUnitLabel}
                    stockQuantity={stockDialog.stockQuantity}
                    targetQty={reduceQty}
                    picks={reduceLotPicks}
                    onPicksChange={setReduceLotPicks}
                  />
                </div>
              ) : reduceQty > 0 ? (
                <p className="flex items-start gap-2 rounded-xl border border-secondary/30 border-dashed bg-muted/20 p-3 text-muted-foreground text-xs">
                  <TriangleAlertIcon className="mt-0.5 size-4 shrink-0" />
                  ບໍ່ມີ lot — ຈະຫຼຸດຈາກສະຕັອກທີ່ບໍ່ມີ lot
                </p>
              ) : null}

              {withoutLot > 0 && lotRows.length > 0 ? (
                <div className="flex flex-col gap-2 rounded-xl border border-secondary/25 bg-muted/10 p-3">
                  <p className="font-medium text-sm">
                    ຈັດສະຕັອກທີ່ບໍ່ມີ lot ເຂົ້າ lot
                  </p>
                  <p className="text-muted-foreground text-xs">
                    ສະຕັອກລວມບໍ່ປ່ຽນ — ແບ່ງຈຳນວນທີ່ບໍ່ມີ lot ໃສ່ lot ທີ່ມີຢູ່
                  </p>
                  <StockAdjustLotPicker
                    mode="assign"
                    variantId={stockDialog.variantId}
                    stockUnitLabel={stockDialog.stockUnitLabel}
                    stockQuantity={stockDialog.stockQuantity}
                    targetQty={withoutLot}
                    picks={assignLotPicks}
                    onPicksChange={setAssignLotPicks}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={closeStockDialog}
            >
              ຍົກເລີກ
            </Button>
            {canSubmitAssign ? (
              <Button
                variant="outline"
                className="rounded-full"
                isLoading={adjustStock.isPending}
                disabled={!canSubmitAssign}
                onClick={() => {
                  if (!stockDialog || !canSubmitAssign) return;
                  adjustStock.mutate(
                    {
                      variantId: stockDialog.variantId,
                      quantityDelta: 0,
                      lotPicks: toApiLotPicks(assignLotPicks),
                    },
                    {
                      onSuccess: () => {
                        toast.success("ຈັດເຂົ້າ lot ສຳເລັດ");
                        closeStockDialog();
                        onStockAdjusted?.();
                      },
                    },
                  );
                }}
              >
                ຈັດເຂົ້າ lot ({assignPickSum})
              </Button>
            ) : null}
            <Button
              className="rounded-full"
              isLoading={adjustStock.isPending}
              disabled={!canSubmitAdjust}
              onClick={() => {
                if (!stockDialog) return;
                const delta = Number(stockDialog.delta);
                if (!Number.isFinite(delta) || delta === 0) {
                  toast.error("ກະລຸນາໃສ່ຈຳນວນທີ່ບໍ່ເທົ່າ 0");
                  return;
                }
                if (delta < 0 && !reduceLotSelectionValid) {
                  toast.error("ເລືອກ lot ບໍ່ຖືກຕ້ອງ");
                  return;
                }
                if (delta > 0 && addPickSum > 0 && addPickSum !== addQty) {
                  toast.error("ແບ່ງເຂົ້າ lot ບໍ່ຄົບ");
                  return;
                }
                if (delta < 0 && Math.abs(delta) > stockDialog.stockQuantity) {
                  toast.error("ຫຼຸດຫຼາຍກວ່າສະຕັອກທີ່ມີ");
                  return;
                }

                const apiLotPicks =
                  delta < 0 && sumLotPickQuantity(reduceLotPicks) > 0
                    ? toApiLotPicks(reduceLotPicks)
                    : delta > 0 && addPickSum > 0
                      ? toApiLotPicks(addLotPicks)
                      : undefined;

                adjustStock.mutate(
                  {
                    variantId: stockDialog.variantId,
                    quantityDelta: delta,
                    lotPicks: apiLotPicks,
                  },
                  {
                    onSuccess: () => {
                      toast.success(
                        delta > 0 ? "ເພີ່ມສະຕັອກສຳເລັດ" : "ຫຼຸດສະຕັອກສຳເລັດ",
                      );
                      closeStockDialog();
                      onStockAdjusted?.();
                    },
                  },
                );
              }}
            >
              {addQty > 0 ? "ບັນທຶກການເພີ່ມ" : "ບັນທຶກການຫຼຸດ"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
